import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import { ethers } from 'ethers';
import { ACTIVE } from '../config/networks';
import { isCorrectChain, switchToActiveNetwork } from '../utils/network';
import { useToast } from './ToastContext';

const Web3Context = createContext(null);

export function useWeb3() {
  return useContext(Web3Context);
}

// Detect the injected provider (Trust Wallet, MetaMask, Binance, etc.)
function getInjected() {
  if (typeof window === 'undefined') return null;
  const eth = window.ethereum;
  if (!eth) return null;
  // If multiple providers are injected, prefer one but keep the raw object.
  return eth;
}

export function Web3Provider({ children }) {
  const toast = useToast();

  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Keep a single provider/signer reference. We rebuild only when account/chain change.
  const ethRef = useRef(getInjected());

  const correctChain = isCorrectChain(chainId);

  // Read-only fallback provider (so the app can show data even before connect).
  const readProvider = useMemo(() => {
    try {
      return new ethers.providers.JsonRpcProvider(ACTIVE.rpcUrls[0]);
    } catch {
      return null;
    }
  }, []);

  // Browser provider + signer (only valid when connected & on right chain).
  const provider = useMemo(() => {
    const eth = ethRef.current;
    if (!eth || !account) return readProvider;
    return new ethers.providers.Web3Provider(eth, 'any');
    // chainId intentionally included so the provider is rebuilt on chain switch
  }, [account, chainId, readProvider]);

  const signer = useMemo(() => {
    if (!account || !correctChain) return null;
    try {
      return provider.getSigner();
    } catch {
      return null;
    }
  }, [provider, account, correctChain]);

  // ---- Connect wallet ----
  const connect = useCallback(async () => {
    const eth = getInjected();
    if (!eth) {
      toast.error('No wallet detected. Open inside Trust Wallet or MetaMask browser.');
      return;
    }
    ethRef.current = eth;
    setConnecting(true);
    try {
      const accounts = await eth.request({ method: 'eth_requestAccounts' });
      const cid = await eth.request({ method: 'eth_chainId' });
      setAccount(accounts && accounts[0] ? ethers.utils.getAddress(accounts[0]) : null);
      setChainId(parseInt(cid, 16));
    } catch (err) {
      if (err && err.code === 4001) toast.error('Connection rejected.');
      else toast.error('Could not connect wallet.');
    } finally {
      setConnecting(false);
    }
  }, [toast]);

  const disconnect = useCallback(() => {
    setAccount(null);
  }, []);

  // ---- Force-switch to the correct network (no page refresh) ----
  const ensureNetwork = useCallback(async () => {
    const eth = ethRef.current || getInjected();
    if (!eth) return;
    setSwitching(true);
    try {
      await switchToActiveNetwork(eth);
      // chainChanged listener updates state; no reload needed.
    } catch (err) {
      if (err && err.code === 4001) toast.error('Network switch rejected.');
      else toast.error('Could not switch network. Do it manually in your wallet.');
    } finally {
      setSwitching(false);
    }
  }, [toast]);

  // ---- Wallet event listeners (NO reload, NO callback loops) ----
  useEffect(() => {
    const eth = ethRef.current || getInjected();
    if (!eth || !eth.on) return;

    const onAccounts = (accs) => {
      if (!accs || accs.length === 0) {
        setAccount(null);
      } else {
        setAccount(ethers.utils.getAddress(accs[0]));
      }
    };
    const onChain = (cid) => {
      // Just update state. React re-renders the guard. No window.location.reload().
      setChainId(parseInt(cid, 16));
    };

    eth.on('accountsChanged', onAccounts);
    eth.on('chainChanged', onChain);

    return () => {
      if (eth.removeListener) {
        eth.removeListener('accountsChanged', onAccounts);
        eth.removeListener('chainChanged', onChain);
      }
    };
  }, []); // bind once

  // ---- Eager reconnect if wallet already authorized ----
  useEffect(() => {
    const eth = getInjected();
    if (!eth) return;
    ethRef.current = eth;
    let cancelled = false;
    (async () => {
      try {
        const accounts = await eth.request({ method: 'eth_accounts' });
        if (cancelled) return;
        if (accounts && accounts.length) {
          const cid = await eth.request({ method: 'eth_chainId' });
          setAccount(ethers.utils.getAddress(accounts[0]));
          setChainId(parseInt(cid, 16));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Auto-prompt network switch when connected on the wrong chain ----
  const promptedRef = useRef(false);
  useEffect(() => {
    if (account && chainId != null && !correctChain && !promptedRef.current) {
      promptedRef.current = true;
      ensureNetwork().finally(() => {
        // allow re-prompt later if they bounce back to a wrong chain
        promptedRef.current = false;
      });
    }
  }, [account, chainId, correctChain, ensureNetwork]);

  const value = {
    account,
    chainId,
    correctChain,
    connecting,
    switching,
    provider,
    readProvider,
    signer,
    connect,
    disconnect,
    ensureNetwork,
    isConnected: !!account,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
}
